import type { CSSProperties, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { t, type MessageKey } from '@/i18n';
import { naicChecklistLinks } from './lighthouse/naicChecklistLinks';

/*
 * Faithful port of the approved Lighthouse prototype (#14368). Colors, sizes,
 * spacing and grid rules are copied from the prototype as fixed values, and
 * the page always renders its light design — do not swap these for site
 * theme tokens without design sign-off.
 */
const C = {
  navy: '#1E2761',
  deep: '#141A45',
  gold: '#D9A441',
  goldInk: '#8A5A00',
  goldBtnText: '#231A05',
  ink: '#16203B',
  ink2: '#4A5670',
  line: '#D8E0EE',
  lineSoft: '#EDF1F8',
  ground: '#F7F9FC',
  ice: '#E4EDFB',
  white: '#FFFFFF',
  critical: '#B3261E',
  high: '#A15C07',
  medium: '#2557C7',
  low: '#1F7A4C',
  customer: '#5B3A9A',
  okInk: '#145C38',
  warnInk: '#7A4A00',
} as const;

const ARCHIVO = 'Archivo, sans-serif';
// Undo site-wide rules the prototype was not designed with: Tailwind's global
// border-box sizing, and balanced, tightened headings. Inline styles still win.
const RESET_CSS =
  '.lh-page,.lh-page *{box-sizing:content-box}' +
  '.lh-page h1,.lh-page h2,.lh-page h3{text-wrap:wrap;letter-spacing:normal}';
const MONO = "'IBM Plex Mono', monospace";

type Risk = 'critical' | 'high' | 'medium' | 'low';
const RISK_BG: Record<Risk, string> = {
  critical: C.critical,
  high: C.high,
  medium: C.medium,
  low: C.low,
};

const FRAMEWORKS = ['nist', 'euAiAct', 'iso42001'] as const;
const STATS = ['naic', 'silos', 'sprawl'] as const;
const BUYERS = ['cio', 'cco', 'ai', 'cdo'] as const;
const DISCOVER_STEPS = ['discover', 'triage', 'enroll'] as const;
const QUESTIONS = ['inventory', 'outcome', 'bias', 'change', 'thirdParty'] as const;
const PATH = ['intake', 'review', 'redTeam', 'production', 'monitoring'] as const;
const GOV_CARDS = ['intake', 'review', 'redTeam', 'guardrails', 'anomaly', 'explain'] as const;
const ENTERPRISE = ['deployment', 'identity', 'secrets', 'fields', 'routing', 'siem'] as const;
const WHY_ROWS = ['discover', 'runtime', 'lineage', 'naic'] as const;
const TRIAL_STEPS = ['connect', 'review', 'report'] as const;

// Illustrative sample rows for the product mock-ups (identifiers, not copy).
const DISCOVERED: ReadonlyArray<{
  name: string;
  risk: Risk;
  source: MessageKey;
  owner: MessageKey;
  ownerChip?: 'warn' | 'ok';
}> = [
  {
    name: 'fraud-scorer-v2',
    risk: 'critical',
    source: 'lighthouse.discover.table.sourceVendor',
    owner: 'lighthouse.discover.table.ownerUnowned',
    ownerChip: 'warn',
  },
  {
    name: 'underwriting-copilot',
    risk: 'high',
    source: 'lighthouse.discover.table.sourceCloud',
    owner: 'lighthouse.discover.table.ownerMlPlatform',
  },
  {
    name: 'triage-router.partner.com',
    risk: 'high',
    source: 'lighthouse.discover.table.sourceA2a',
    owner: 'lighthouse.discover.table.ownerPartner',
  },
  {
    name: 'Claims Intake Agent',
    risk: 'medium',
    source: 'lighthouse.discover.table.sourceRegistry',
    owner: 'lighthouse.discover.table.ownerEnrolled',
    ownerChip: 'ok',
  },
];

const LINEAGE: ReadonlyArray<{
  skill: string;
  version: string;
  risk: Risk;
  customer: boolean;
  model: string;
  ref: string;
  review: boolean;
}> = [
  {
    skill: 'siu-fraud-scoring',
    version: 'v5.0.1',
    risk: 'critical',
    customer: true,
    model: 'anthropic · claude-opus-4-8',
    ref: 'CLM-2026-008295',
    review: true,
  },
  {
    skill: 'quote-pricing',
    version: 'v5.1.0',
    risk: 'high',
    customer: true,
    model: 'anthropic · claude-opus-4-8',
    ref: 'QT-2026-44712',
    review: true,
  },
  {
    skill: 'pii-redaction',
    version: 'v2.2.0',
    risk: 'low',
    customer: false,
    model: 'anthropic · claude-haiku-4-5',
    ref: '—',
    review: false,
  },
];

// ---- shared prototype styles -------------------------------------------------

const container = (padding: string): CSSProperties => ({
  maxWidth: 1200,
  margin: '0 auto',
  padding,
});
const autoGrid = (min: number, gap: number): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
  gap,
});
const eyebrow = (color: string): CSSProperties => ({
  fontFamily: MONO,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color,
});
const h2Small: CSSProperties = {
  fontFamily: ARCHIVO,
  fontWeight: 800,
  fontSize: 'clamp(28px, 3vw, 38px)',
  letterSpacing: '-0.01em',
  margin: 0,
};
const h2Section: CSSProperties = {
  fontFamily: ARCHIVO,
  fontWeight: 800,
  fontSize: 'clamp(30px, 3.2vw, 42px)',
  lineHeight: 1.1,
  letterSpacing: '-0.01em',
  margin: '12px 0 0',
};
const sectionLead: CSSProperties = {
  fontSize: 18,
  lineHeight: 1.6,
  color: C.ink2,
  margin: '18px 0 0',
};
const card: CSSProperties = {
  background: C.white,
  border: `1px solid ${C.line}`,
  borderRadius: 12,
  padding: 24,
};
const cardBody: CSSProperties = {
  fontSize: 16,
  lineHeight: 1.55,
  color: C.ink2,
  margin: '10px 0 0',
};
const cardTitle: CSSProperties = {
  fontFamily: ARCHIVO,
  fontWeight: 700,
  fontSize: 19,
  lineHeight: 1.3,
};
const btnBase: CSSProperties = {
  fontSize: 17,
  padding: '14px 24px',
  borderRadius: 8,
  textDecoration: 'none',
  minHeight: 48,
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
};
const goldBtn: CSSProperties = {
  ...btnBase,
  background: C.gold,
  color: C.goldBtnText,
  fontWeight: 700,
};
const navyBtn: CSSProperties = {
  ...btnBase,
  background: C.navy,
  color: C.white,
  fontWeight: 600,
};
const ghostBtn: CSSProperties = {
  ...btnBase,
  border: '1px solid rgba(255,255,255,0.35)',
  color: C.white,
  fontWeight: 600,
};
const tableHead: CSSProperties = {
  fontFamily: MONO,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: C.ink2,
};
const monoSmall: CSSProperties = { fontFamily: MONO, fontSize: 12, color: C.ink2 };
const pill = (border: string, color: string): CSSProperties => ({
  fontSize: 14,
  fontWeight: 600,
  padding: '6px 12px',
  borderRadius: 999,
  border: `1px solid ${border}`,
  color,
});

function Chip({
  bg,
  children,
  pad = '3px 8px',
}: {
  bg: string;
  children: ReactNode;
  pad?: string;
}) {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        padding: pad,
        borderRadius: 5,
        background: bg,
        color: C.white,
      }}
    >
      {children}
    </span>
  );
}

function RiskChip({ risk }: { risk: Risk }) {
  return <Chip bg={RISK_BG[risk]}>{t(`lighthouse.risk.${risk}`)}</Chip>;
}

function Arrow() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#8A97B4"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  );
}

// ---- product mock-ups --------------------------------------------------------

function DecisionRecord() {
  const rows: ReadonlyArray<[MessageKey, ReactNode, CSSProperties?]> = [
    ['lighthouse.record.inputsLabel', t('lighthouse.record.inputsValue')],
    ['lighthouse.record.modelLabel', 'anthropic · claude-sonnet-5', { fontFamily: MONO }],
    ['lighthouse.record.reviewLabel', t('lighthouse.record.reviewValue')],
    ['lighthouse.record.outcomeLabel', t('lighthouse.record.outcomeValue'), { fontWeight: 600 }],
  ];
  return (
    <div style={{ position: 'relative' }} role="img" aria-label={t('lighthouse.record.ariaLabel')}>
      <div
        style={{
          background: C.white,
          color: C.ink,
          borderRadius: 14,
          boxShadow: '0 30px 60px -20px rgba(5,10,40,0.6)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 20px',
            background: '#F1F4FA',
            borderBottom: `1px solid ${C.line}`,
          }}
        >
          <span
            style={{
              ...monoSmall,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {t('lighthouse.record.label')}
          </span>
          <span style={monoSmall}>CLM-2026-008421</span>
        </div>
        <div style={{ padding: '22px 22px 8px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: ARCHIVO, fontWeight: 700, fontSize: 20 }}>
              claims-triage-route
            </span>
            <span style={monoSmall}>v4.1.2</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            <Chip bg={C.high} pad="3px 9px">
              {t('lighthouse.record.riskHigh')}
            </Chip>
            <Chip bg={C.customer} pad="3px 9px">
              {t('lighthouse.record.customerImpacting')}
            </Chip>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                padding: '3px 9px',
                borderRadius: 5,
                background: C.ice,
                color: C.navy,
              }}
            >
              {t('lighthouse.record.naicComplete')}
            </span>
          </div>
        </div>
        <div style={{ padding: '8px 22px 6px', display: 'grid', gridTemplateColumns: '140px 1fr' }}>
          {rows.map(([label, value, extra], i) => {
            const cell: CSSProperties = {
              fontSize: 14,
              padding: '10px 0',
              borderBottom: i === rows.length - 1 ? 'none' : `1px solid ${C.lineSoft}`,
            };
            return [
              <div key={`${label}-l`} style={{ ...cell, color: C.ink2 }}>
                {t(label)}
              </div>,
              <div key={`${label}-v`} style={{ ...cell, ...extra }}>
                {value}
              </div>,
            ];
          })}
        </div>
        <div
          style={{
            margin: '6px 22px 22px',
            padding: '12px 14px',
            borderRadius: 8,
            background: '#EAF5EF',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={C.low}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
          <span style={{ fontSize: 14, color: C.okInk, fontWeight: 600 }}>
            {t('lighthouse.record.integrity')}
          </span>
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          left: -28,
          bottom: -34,
          background: C.white,
          color: C.ink,
          borderRadius: 12,
          boxShadow: '0 18px 40px -16px rgba(5,10,40,0.55)',
          padding: '14px 16px',
          width: 280,
          border: `1px solid ${C.line}`,
        }}
      >
        <div
          style={{
            ...monoSmall,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {t('lighthouse.record.discoveredLabel')}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginTop: 8,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 15 }}>fraud-scorer-v2</span>
          <RiskChip risk="critical" />
        </div>
        <div style={{ fontSize: 13, color: C.ink2, marginTop: 4 }}>
          {t('lighthouse.record.discoveredMeta')}
        </div>
      </div>
    </div>
  );
}

function DiscoveredTable() {
  const cols: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '2.2fr 0.9fr 1.3fr 1.2fr',
    gap: 10,
  };
  return (
    <div
      style={{ background: C.ground, border: `1px solid ${C.line}`, borderRadius: 14, padding: 20 }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span style={{ fontFamily: ARCHIVO, fontWeight: 700, fontSize: 18 }}>
          {t('lighthouse.discover.table.title')}
        </span>
        <span style={monoSmall}>{t('lighthouse.discover.table.summary')}</span>
      </div>
      <div
        style={{
          overflowX: 'auto',
          marginTop: 14,
          background: C.white,
          border: `1px solid ${C.line}`,
          borderRadius: 10,
        }}
      >
        <div style={{ minWidth: 520 }}>
          <div
            style={{
              ...cols,
              ...tableHead,
              padding: '11px 16px',
              borderBottom: `1px solid ${C.line}`,
            }}
          >
            <span>{t('lighthouse.discover.table.colAgent')}</span>
            <span>{t('lighthouse.discover.table.colRisk')}</span>
            <span>{t('lighthouse.discover.table.colSource')}</span>
            <span>{t('lighthouse.discover.table.colOwner')}</span>
          </div>
          {DISCOVERED.map((row, i) => (
            <div
              key={row.name}
              style={{
                ...cols,
                padding: '13px 16px',
                fontSize: 14,
                alignItems: 'center',
                borderBottom: i === DISCOVERED.length - 1 ? 'none' : `1px solid ${C.lineSoft}`,
              }}
            >
              <span style={{ fontWeight: 600 }}>{row.name}</span>
              <span>
                <RiskChip risk={row.risk} />
              </span>
              <span style={{ color: C.ink2 }}>{t(row.source)}</span>
              {row.ownerChip ? (
                <span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      padding: '3px 8px',
                      borderRadius: 5,
                      background: row.ownerChip === 'warn' ? '#FDF0DA' : '#E6F4EC',
                      color: row.ownerChip === 'warn' ? C.warnInk : C.okInk,
                    }}
                  >
                    {t(row.owner)}
                  </span>
                </span>
              ) : (
                <span style={{ color: C.ink2 }}>{t(row.owner)}</span>
              )}
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          marginTop: 16,
          padding: '14px 16px',
          borderRadius: 10,
          background: C.navy,
          color: C.white,
          fontSize: 15,
          lineHeight: 1.5,
        }}
      >
        <strong style={{ color: C.gold }}>{t('lighthouse.discover.failClosedLead')}</strong>{' '}
        {t('lighthouse.discover.failClosedBody')}
      </div>
    </div>
  );
}

function LineageTable() {
  const cols: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1.3fr 1.6fr 1.3fr 1fr',
    gap: 10,
  };
  const filter: CSSProperties = {
    fontSize: 13,
    padding: '5px 10px',
    borderRadius: 6,
    border: `1px solid ${C.line}`,
    color: C.ink2,
  };
  return (
    <div
      style={{
        marginTop: 40,
        background: C.white,
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '16px 20px',
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        <span style={{ fontFamily: ARCHIVO, fontWeight: 700, fontSize: 18 }}>
          {t('lighthouse.compliance.lineage.title')}
        </span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <span style={filter}>{t('lighthouse.compliance.lineage.filterCustomer')}</span>
          <span style={filter}>{t('lighthouse.compliance.lineage.filterRisk')}</span>
          <span
            style={{
              fontSize: 13,
              padding: '5px 10px',
              borderRadius: 6,
              background: C.ink,
              color: C.white,
              fontWeight: 600,
            }}
          >
            {t('lighthouse.compliance.lineage.export')}
          </span>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 760 }}>
          <div
            style={{
              ...cols,
              ...tableHead,
              padding: '11px 20px',
              background: C.ground,
              borderBottom: `1px solid ${C.line}`,
            }}
          >
            <span>{t('lighthouse.compliance.lineage.colSkill')}</span>
            <span>{t('lighthouse.compliance.lineage.colRisk')}</span>
            <span>{t('lighthouse.compliance.lineage.colModel')}</span>
            <span>{t('lighthouse.compliance.lineage.colRef')}</span>
            <span>{t('lighthouse.compliance.lineage.colReview')}</span>
          </div>
          {LINEAGE.map((row, i) => (
            <div
              key={row.skill}
              style={{
                ...cols,
                padding: '13px 20px',
                fontSize: 14,
                alignItems: 'center',
                borderBottom: i === LINEAGE.length - 1 ? 'none' : `1px solid ${C.lineSoft}`,
              }}
            >
              <span>
                <strong>{row.skill}</strong> <span style={monoSmall}>{row.version}</span>
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                <RiskChip risk={row.risk} />
                {row.customer && <Chip bg={C.customer}>{t('lighthouse.risk.customer')}</Chip>}
              </span>
              <span style={{ fontFamily: MONO, fontSize: 13 }}>{row.model}</span>
              <span style={{ fontFamily: MONO, fontSize: 13, color: C.ink2 }}>{row.ref}</span>
              <span
                style={
                  row.review
                    ? { fontSize: 13, fontWeight: 600, color: C.warnInk }
                    : { fontSize: 13, color: C.ink2 }
                }
              >
                {t(
                  row.review
                    ? 'lighthouse.compliance.lineage.reviewRequired'
                    : 'lighthouse.compliance.lineage.reviewNone'
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- page --------------------------------------------------------------------

export function LighthousePage() {
  return (
    <>
      <Helmet>
        <title>{t('lighthouse.meta.title')}</title>
        <meta name="description" content={t('lighthouse.meta.description')} />
        <meta name="keywords" content={t('lighthouse.meta.keywords')} />
        <meta property="og:title" content={t('lighthouse.meta.ogTitle')} />
        <meta property="og:description" content={t('lighthouse.meta.ogDescription')} />
        <link rel="canonical" href="https://www.criticalbridge.ai/lighthouse" />
        {/* Weights the prototype uses that the site-wide font link omits. */}
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@700&family=IBM+Plex+Mono:wght@600&display=swap"
        />
      </Helmet>

      <style>{RESET_CSS}</style>
      <div
        className="lh-page"
        style={{
          fontFamily: "'Source Sans 3', -apple-system, sans-serif",
          lineHeight: 'normal',
          color: C.ink,
          background: C.ground,
          width: '100%',
          overflowX: 'clip',
        }}
      >
        {/* Hero */}
        <section id="top" style={{ background: C.navy, color: C.white }}>
          <div
            style={{ ...container('88px 24px 72px'), ...autoGrid(480, 56), alignItems: 'center' }}
          >
            <div>
              <div style={eyebrow(C.gold)}>{t('lighthouse.hero.eyebrow')}</div>
              <h1
                style={{
                  fontFamily: ARCHIVO,
                  fontWeight: 800,
                  fontSize: 'clamp(40px, 5vw, 60px)',
                  lineHeight: 1.04,
                  letterSpacing: '-0.02em',
                  margin: '18px 0 0',
                }}
              >
                {t('lighthouse.hero.title')}
              </h1>
              <p
                style={{
                  fontSize: 20,
                  lineHeight: 1.55,
                  color: 'rgba(255,255,255,0.78)',
                  margin: '22px 0 0',
                  maxWidth: '34em',
                }}
              >
                {t('lighthouse.hero.body')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 34 }}>
                <Link to="/contact" style={goldBtn}>
                  {t('lighthouse.hero.primaryCta')}
                </Link>
                <a href="#start" style={ghostBtn}>
                  {t('lighthouse.hero.secondaryCta')}
                </a>
              </div>
              <div
                style={{
                  marginTop: 40,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px 10px',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    fontFamily: MONO,
                    fontSize: 12,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.6)',
                    marginRight: 4,
                  }}
                >
                  {t('lighthouse.hero.mappedTo')}
                </span>
                <span style={pill('rgba(217,164,65,0.55)', '#F2D8A7')}>
                  {t('lighthouse.hero.frameworks.naic')}
                </span>
                {FRAMEWORKS.map((f) => (
                  <span key={f} style={pill('rgba(255,255,255,0.22)', 'rgba(255,255,255,0.85)')}>
                    {t(`lighthouse.hero.frameworks.${f}`)}
                  </span>
                ))}
              </div>
            </div>
            <DecisionRecord />
          </div>
        </section>

        {/* Stats */}
        <section style={{ background: C.white, borderBottom: `1px solid ${C.line}` }}>
          <div style={container('72px 24px')}>
            <h2 style={{ ...h2Small, maxWidth: '22em' }}>{t('lighthouse.stats.title')}</h2>
            <div style={{ ...autoGrid(240, 32), marginTop: 40 }}>
              {STATS.map((s) => (
                <div key={s}>
                  <div
                    style={{
                      fontFamily: ARCHIVO,
                      fontWeight: 800,
                      fontSize: 52,
                      color: C.navy,
                      lineHeight: 1,
                    }}
                  >
                    {t(`lighthouse.stats.${s}.value`)}
                  </div>
                  <p style={{ fontSize: 17, lineHeight: 1.5, margin: '12px 0 0' }}>
                    {t(`lighthouse.stats.${s}.text`)}
                  </p>
                  <div style={{ ...monoSmall, marginTop: 8 }}>
                    {t(`lighthouse.stats.${s}.source`)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Buyers */}
        <section style={{ background: C.ground }}>
          <div style={container('80px 24px')}>
            <div style={eyebrow(C.goldInk)}>{t('lighthouse.buyers.eyebrow')}</div>
            <h2 style={{ ...h2Small, margin: '10px 0 0', maxWidth: '24em' }}>
              {t('lighthouse.buyers.title')}
            </h2>
            <div style={{ ...autoGrid(250, 16), marginTop: 36 }}>
              {BUYERS.map((b) => (
                <div key={b} style={card}>
                  <div
                    style={{
                      fontFamily: MONO,
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      color: C.ink2,
                      textTransform: 'uppercase',
                    }}
                  >
                    {t(`lighthouse.buyers.${b}.role`)}
                  </div>
                  <h3
                    style={{
                      fontFamily: ARCHIVO,
                      fontSize: 20,
                      fontWeight: 700,
                      margin: '10px 0 0',
                    }}
                  >
                    {t(`lighthouse.buyers.${b}.title`)}
                  </h3>
                  <p style={{ fontSize: 16, lineHeight: 1.5, color: C.ink2, margin: '8px 0 0' }}>
                    {t(`lighthouse.buyers.${b}.body`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 01 Discover & Enroll */}
        <section id="discover" style={{ background: C.white, borderTop: `1px solid ${C.line}` }}>
          <div style={container('88px 24px')}>
            <div style={{ ...autoGrid(440, 56), alignItems: 'start' }}>
              <div>
                <div style={eyebrow(C.goldInk)}>{t('lighthouse.discover.eyebrow')}</div>
                <h2 style={h2Section}>{t('lighthouse.discover.title')}</h2>
                <p style={sectionLead}>{t('lighthouse.discover.body')}</p>
                <ol
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '32px 0 0',
                    display: 'grid',
                    gap: 18,
                  }}
                >
                  {DISCOVER_STEPS.map((s, i) => (
                    <li
                      key={s}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '40px 1fr',
                        gap: 14,
                        alignItems: 'start',
                      }}
                    >
                      <span
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: C.navy,
                          color: C.gold,
                          fontFamily: ARCHIVO,
                          fontWeight: 800,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {i + 1}
                      </span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 17 }}>
                          {t(`lighthouse.discover.steps.${s}.title`)}
                        </div>
                        <div style={{ fontSize: 16, lineHeight: 1.5, color: C.ink2 }}>
                          {t(`lighthouse.discover.steps.${s}.body`)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <DiscoveredTable />
            </div>
          </div>
        </section>

        {/* 02 Compliance */}
        <section id="compliance" style={{ background: C.ground, borderTop: `1px solid ${C.line}` }}>
          <div style={container('88px 24px')}>
            <div style={eyebrow(C.goldInk)}>{t('lighthouse.compliance.eyebrow')}</div>
            <h2 style={{ ...h2Section, maxWidth: '20em' }}>{t('lighthouse.compliance.title')}</h2>
            <p style={{ ...sectionLead, maxWidth: '44em' }}>{t('lighthouse.compliance.body')}</p>
            <div style={{ ...autoGrid(340, 16), marginTop: 40 }}>
              {QUESTIONS.map((q) => (
                <div key={q} style={card}>
                  <div style={cardTitle}>{t(`lighthouse.compliance.questions.${q}.q`)}</div>
                  <p style={cardBody}>{t(`lighthouse.compliance.questions.${q}.a`)}</p>
                </div>
              ))}
              <div style={{ background: C.navy, borderRadius: 12, padding: 24, color: C.white }}>
                <div style={{ ...cardTitle, color: C.gold }}>
                  {t('lighthouse.compliance.questions.evidence.q')}
                </div>
                <p style={{ ...cardBody, color: 'rgba(255,255,255,0.82)' }}>
                  {t('lighthouse.compliance.questions.evidence.a')}
                </p>
              </div>
            </div>
            <LineageTable />
            <div
              style={{
                ...card,
                marginTop: 40,
                borderLeft: `4px solid ${C.gold}`,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 20,
              }}
            >
              <div style={{ maxWidth: '60ch' }}>
                <div style={cardTitle}>{t('lighthouse.checklist.promo.title')}</div>
                <p style={cardBody}>{t('lighthouse.checklist.promo.body')}</p>
              </div>
              <Link to={naicChecklistLinks.checklistFromLighthouse} style={navyBtn}>
                {t('lighthouse.checklist.promo.cta')}
              </Link>
            </div>
          </div>
        </section>

        {/* 03 AI Governance */}
        <section id="governance" style={{ background: C.white, borderTop: `1px solid ${C.line}` }}>
          <div style={container('88px 24px')}>
            <div style={eyebrow(C.goldInk)}>{t('lighthouse.governance.eyebrow')}</div>
            <h2 style={{ ...h2Section, maxWidth: '22em' }}>{t('lighthouse.governance.title')}</h2>
            <p style={{ ...sectionLead, maxWidth: '44em' }}>{t('lighthouse.governance.body')}</p>
            <ol
              aria-label={t('lighthouse.governance.path.ariaLabel')}
              style={{
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                margin: '32px 0 0',
                alignItems: 'center',
              }}
            >
              {PATH.map((p, i) => (
                <li key={p} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {i > 0 && <Arrow />}
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      padding: '10px 16px',
                      borderRadius: 999,
                      background: p === 'production' ? C.navy : C.ice,
                      color: p === 'production' ? C.white : C.navy,
                    }}
                  >
                    {t(`lighthouse.governance.path.${p}`)}
                  </span>
                </li>
              ))}
            </ol>
            <div style={{ ...autoGrid(330, 16), marginTop: 36 }}>
              {GOV_CARDS.map((c) => (
                <div
                  key={c}
                  style={{ border: `1px solid ${C.line}`, borderRadius: 12, padding: 24 }}
                >
                  <h3 style={{ fontFamily: ARCHIVO, fontSize: 19, fontWeight: 700, margin: 0 }}>
                    {t(`lighthouse.governance.cards.${c}.title`)}
                  </h3>
                  <p style={{ fontSize: 16, lineHeight: 1.55, color: C.ink2, margin: '8px 0 0' }}>
                    {t(`lighthouse.governance.cards.${c}.body`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 04 Enterprise-grade */}
        <section id="enterprise" style={{ background: C.deep, color: C.white }}>
          <div style={{ ...container('88px 24px'), ...autoGrid(400, 48), alignItems: 'start' }}>
            <div>
              <div style={eyebrow(C.gold)}>{t('lighthouse.enterprise.eyebrow')}</div>
              <h2 style={h2Section}>{t('lighthouse.enterprise.title')}</h2>
              <p style={{ ...sectionLead, color: 'rgba(255,255,255,0.75)' }}>
                {t('lighthouse.enterprise.body')}
              </p>
            </div>
            <div style={autoGrid(240, 12)}>
              {ENTERPRISE.map((e) => (
                <div
                  key={e}
                  style={{
                    border: '1px solid rgba(255,255,255,0.16)',
                    borderRadius: 10,
                    padding: '16px 18px',
                    fontSize: 16,
                    lineHeight: 1.4,
                  }}
                >
                  <strong style={{ display: 'block', color: C.white }}>
                    {t(`lighthouse.enterprise.items.${e}.title`)}
                  </strong>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {t(`lighthouse.enterprise.items.${e}.body`)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Lighthouse */}
        <section style={{ background: C.ground }}>
          <div style={container('88px 24px')}>
            <div style={eyebrow(C.goldInk)}>{t('lighthouse.why.eyebrow')}</div>
            <h2 style={{ ...h2Section, maxWidth: '22em' }}>{t('lighthouse.why.title')}</h2>
            <div
              style={{
                overflowX: 'auto',
                marginTop: 36,
                border: `1px solid ${C.line}`,
                borderRadius: 14,
                background: C.white,
              }}
            >
              <table
                style={{
                  minWidth: 720,
                  width: '100%',
                  borderCollapse: 'collapse',
                  textAlign: 'left',
                }}
              >
                <colgroup>
                  <col style={{ width: '44.4%' }} />
                  <col style={{ width: '18.5%' }} />
                  <col style={{ width: '18.5%' }} />
                  <col style={{ width: '18.6%' }} />
                </colgroup>
                <thead>
                  <tr
                    style={{ borderBottom: `1px solid ${C.line}`, fontWeight: 700, fontSize: 15 }}
                  >
                    <td style={{ padding: '16px 0 16px 24px' }} />
                    <th scope="col" style={{ padding: 16, color: C.ink2, verticalAlign: 'bottom' }}>
                      {t('lighthouse.why.colGovernance')}
                    </th>
                    <th scope="col" style={{ padding: 16, color: C.ink2, verticalAlign: 'bottom' }}>
                      {t('lighthouse.why.colSecurity')}
                    </th>
                    <th
                      scope="col"
                      style={{
                        padding: '16px 24px 16px 16px',
                        color: C.navy,
                        fontFamily: ARCHIVO,
                        fontSize: 17,
                        verticalAlign: 'bottom',
                      }}
                    >
                      {t('lighthouse.why.colLighthouse')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {WHY_ROWS.map((r, i) => (
                    <tr
                      key={r}
                      style={{
                        fontSize: 16,
                        borderBottom:
                          i === WHY_ROWS.length - 1 ? 'none' : `1px solid ${C.lineSoft}`,
                      }}
                    >
                      <th scope="row" style={{ padding: '16px 16px 16px 24px', fontWeight: 400 }}>
                        {t(`lighthouse.why.rows.${r}.label`)}
                      </th>
                      <td style={{ padding: 16, color: C.ink2 }}>
                        {t(`lighthouse.why.rows.${r}.governance`)}
                      </td>
                      <td style={{ padding: 16, color: C.ink2 }}>
                        {t(`lighthouse.why.rows.${r}.security`)}
                      </td>
                      <td
                        style={{ padding: '16px 24px 16px 16px', fontWeight: 700, color: C.okInk }}
                      >
                        {t('lighthouse.why.yes')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 13, color: C.ink2, margin: '12px 0 0' }}>
              {t('lighthouse.why.footnote')}
            </p>
          </div>
        </section>

        {/* Discovery Trial */}
        <section id="start" style={{ background: C.white, borderTop: `1px solid ${C.line}` }}>
          <div style={container('88px 24px')}>
            <div style={eyebrow(C.goldInk)}>{t('lighthouse.trial.eyebrow')}</div>
            <h2 style={h2Section}>{t('lighthouse.trial.title')}</h2>
            <p
              style={{
                fontSize: 18,
                lineHeight: 1.55,
                color: C.ink2,
                margin: '14px 0 0',
                maxWidth: '40em',
              }}
            >
              {t('lighthouse.trial.body')}
            </p>
            <div style={{ ...autoGrid(280, 24), marginTop: 36 }}>
              {TRIAL_STEPS.map((s, i) => (
                <div key={s} style={{ borderTop: `3px solid ${C.gold}`, paddingTop: 18 }}>
                  <div
                    style={{
                      fontFamily: MONO,
                      fontSize: 13,
                      fontWeight: 600,
                      color: C.goldInk,
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('lighthouse.trial.stepLabel')} {i + 1}
                  </div>
                  <h3
                    style={{
                      fontFamily: ARCHIVO,
                      fontSize: 20,
                      fontWeight: 700,
                      margin: '8px 0 0',
                    }}
                  >
                    {t(`lighthouse.trial.steps.${s}.title`)}
                  </h3>
                  <p style={{ fontSize: 16, lineHeight: 1.55, color: C.ink2, margin: '8px 0 0' }}>
                    {t(`lighthouse.trial.steps.${s}.body`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="briefing" style={{ background: C.navy, color: C.white }}>
          <div
            style={{
              ...container('80px 24px'),
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 32,
            }}
          >
            <div style={{ maxWidth: 640 }}>
              <h2 style={{ ...h2Section, margin: 0 }}>{t('lighthouse.cta.title')}</h2>
              <p style={{ ...sectionLead, margin: '14px 0 0', color: 'rgba(255,255,255,0.75)' }}>
                {t('lighthouse.cta.body')}
              </p>
              <p style={{ fontSize: 15, margin: '18px 0 0', color: 'rgba(255,255,255,0.75)' }}>
                {t('lighthouse.checklist.promo.finalBefore')}
                <Link
                  to={naicChecklistLinks.checklistFromLighthouse}
                  style={{ color: C.gold, fontWeight: 600 }}
                >
                  {t('lighthouse.checklist.promo.finalLink')}
                </Link>
                {t('lighthouse.checklist.promo.finalAfter')}
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <Link to="/contact" style={goldBtn}>
                {t('lighthouse.cta.primary')}
              </Link>
              <Link to="/trial" style={ghostBtn}>
                {t('lighthouse.cta.secondary')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
